import { GRID_COLS, GRID_ROWS } from '../config';
import { ElementInstance } from '../data/elements';

/**
 * Grid manages a 6x6 grid of element instances.
 * Cells are indexed as cells[col][row], where null means an empty cell.
 */
export class Grid {
  private cells: (ElementInstance | null)[][];

  constructor() {
    // Initialize empty 6x6 grid
    this.cells = [];
    for (let col = 0; col < GRID_COLS; col++) {
      this.cells[col] = [];
      for (let row = 0; row < GRID_ROWS; row++) {
        this.cells[col][row] = null;
      }
    }
  }

  /**
   * Get the element at the given position, or null if empty.
   */
  get(col: number, row: number): ElementInstance | null {
    if (!this.isValid(col, row)) return null;
    return this.cells[col][row];
  }

  /**
   * Set an element at the given position, or clear it if null.
   */
  set(col: number, row: number, element: ElementInstance | null): void {
    if (!this.isValid(col, row)) return;
    this.cells[col][row] = element;
  }

  /**
   * Remove and return the element at the given position.
   */
  remove(col: number, row: number): ElementInstance | null {
    if (!this.isValid(col, row)) return null;
    const element = this.cells[col][row];
    this.cells[col][row] = null;
    return element;
  }

  /**
   * Move an element from one position to another.
   * Returns true if successful, false if target is occupied or out of bounds.
   */
  move(
    fromCol: number,
    fromRow: number,
    toCol: number,
    toRow: number
  ): boolean {
    if (!this.isValid(fromCol, fromRow) || !this.isValid(toCol, toRow)) {
      return false;
    }

    // Target must be empty
    if (this.cells[toCol][toRow] !== null) {
      return false;
    }

    // Move element
    const element = this.remove(fromCol, fromRow);
    if (element === null) {
      return false;
    }

    element.col = toCol;
    element.row = toRow;
    this.set(toCol, toRow, element);

    return true;
  }

  /**
   * Find a random empty cell.
   * Returns null if no empty cells exist.
   */
  findEmpty(): { col: number; row: number } | null {
    const empty = this.findAllEmpty();
    if (empty.length === 0) return null;
    return empty[Math.floor(Math.random() * empty.length)];
  }

  /**
   * Find all empty cells.
   */
  findAllEmpty(): { col: number; row: number }[] {
    const empty: { col: number; row: number }[] = [];
    for (let col = 0; col < GRID_COLS; col++) {
      for (let row = 0; row < GRID_ROWS; row++) {
        if (this.cells[col][row] === null) {
          empty.push({ col, row });
        }
      }
    }
    return empty;
  }

  /**
   * Get all placed elements.
   */
  getAll(): ElementInstance[] {
    const all: ElementInstance[] = [];
    for (let col = 0; col < GRID_COLS; col++) {
      for (let row = 0; row < GRID_ROWS; row++) {
        const element = this.cells[col][row];
        if (element !== null) {
          all.push(element);
        }
      }
    }
    return all;
  }

  /**
   * Check if the grid is completely full (no empty cells).
   */
  isFull(): boolean {
    return this.findEmpty() === null;
  }

  /**
   * Check if a specific cell is empty.
   */
  isEmpty(col: number, row: number): boolean {
    if (!this.isValid(col, row)) return false;
    return this.cells[col][row] === null;
  }

  /**
   * Check if a position is within valid grid bounds.
   */
  isValid(col: number, row: number): boolean {
    return col >= 0 && col < GRID_COLS && row >= 0 && row < GRID_ROWS;
  }

  /**
   * Get all 8-directionally adjacent occupied cells.
   */
  getNeighbors8(col: number, row: number): { col: number; row: number; element: ElementInstance }[] {
    const neighbors: { col: number; row: number; element: ElementInstance }[] = [];
    // Clockwise: N, NE, E, SE, S, SW, W, NW
    const offsets = [
      [0, -1], [1, -1], [1, 0], [1, 1],
      [0, 1], [-1, 1], [-1, 0], [-1, -1],
    ];
    for (const [dx, dy] of offsets) {
      const nc = col + dx;
      const nr = row + dy;
      if (!this.isValid(nc, nr)) continue;
      const element = this.cells[nc][nr];
      if (element) {
        neighbors.push({ col: nc, row: nr, element });
      }
    }
    return neighbors;
  }

  /**
   * Clear all elements from the grid.
   */
  clear(): void {
    for (let col = 0; col < GRID_COLS; col++) {
      for (let row = 0; row < GRID_ROWS; row++) {
        this.cells[col][row] = null;
      }
    }
  }
}
